/* One contract: its terms, its two signatures, what the last evaluation found, and what the chain
 * recorded along the way.
 *
 * **The state hint is on the page rather than in a tooltip**, because it is the sentence that stops
 * `draft` reading as broken and `pending_signoff` as stuck. Nothing evaluates in either, and a reader
 * looking at a contract with no verdict needs to know whether that is a gap or the design.
 *
 * **Every action is gated twice, and the two gates say different things.** The contract's *state*
 * decides what is possible; `can()` decides what this caller may be offered. An impossible action is
 * a bordered non-control naming why the lifecycle withholds it; a withheld one is dashed and says it
 * belongs to another role. Folding them — or dropping the second, which this screen used to do —
 * leaves an approver looking at a draft with an empty action row, unable to tell "nothing to do here"
 * from "not yours to do". On top of both, the server refuses a signature from anyone but the declared
 * party for that side with a 403, even with every permission, so a button is an offer and never a
 * claim about who may sign.
 *
 * **The history is read from the audit chain rather than inferred.** An earlier version of this file
 * said a per-contract timeline was impossible and explained why, which was true when it was written:
 * the entries had no route. They do now. Nothing here is built from what this page happened to see.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
  ACTIVATE_CONTRACT_PERMISSION,
  can,
  EVALUATE_CONTRACT_PERMISSION,
  RETIRE_CONTRACT_PERMISSION,
  SIGN_CONTRACT_PERMISSION,
  SUBMIT_CONTRACT_PERMISSION,
  useIdentity,
} from '@/entities/session';
import type { Contract, EvaluationOutcome } from '@/shared/api/generated/contracts';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { SCREEN_PATHS } from '@/shared/routes';

import {
  useContract,
  useContractLifecycle,
  useEvaluateContract,
  useSignContract,
} from '../api/contracts';
import {
  blockedFor,
  gateFor,
  outstandingSides,
  signoffsOf,
  stateHintKey,
  stateLabelKey,
  termRows,
  toneOf,
} from '../model/contracts';
import type { ContractAction } from '../model/contracts';
import { ContractHistory } from './ContractHistory';

/** Which permission each action needs. The split is the backend's: `data-steward` submits and
 * evaluates, `approver` signs, activates and retires — neither role can do the other's half, which
 * is the author/approve separation two-eyes approval exists to hold. */
const NEEDS: Record<ContractAction, string> = {
  submit: SUBMIT_CONTRACT_PERMISSION,
  sign_producer: SIGN_CONTRACT_PERMISSION,
  sign_consumer: SIGN_CONTRACT_PERMISSION,
  activate: ACTIVATE_CONTRACT_PERMISSION,
  evaluate: EVALUATE_CONTRACT_PERMISSION,
  retire: RETIRE_CONTRACT_PERMISSION,
};

function Terms({
  contract,
  evaluation,
}: {
  readonly contract: Contract;
  readonly evaluation: EvaluationOutcome | null;
}): ReactElement {
  const { t } = useTranslation();
  const rows = termRows(contract, evaluation);

  return (
    <div className="lq-table-scroll lq-table-scroll--framed lq-ct__terms">
      <table className="lq-table lq-table--ledger">
        <caption className="lq-visually-hidden">
          {t('contracts.terms.caption', { contract: contract.name })}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('contracts.terms.term')}</th>
            <th scope="col">{t('contracts.terms.promised')}</th>
            <th scope="col">{t('contracts.terms.observed')}</th>
            <th scope="col">{t('contracts.terms.holds')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.kind}
              className={row.holds === false ? 'lq-ct__broken' : undefined}
            >
              <td>
                <strong className="lq-ct__termName">
                  {t(`contracts.term.${row.kind}`)}
                </strong>
                <div className="lq-cell__secondary">
                  {t(`contracts.termNote.${row.kind}`)}
                </div>
              </td>
              <td className="lq-mono lq-nowrap">{row.promised}</td>
              <td className="lq-mono lq-nowrap">
                {/* Three states, rendered as three things. Nothing evaluated says so; a fact the
                    platform does not hold says which fact is missing; only a real measurement is
                    printed as a value. A zero here would claim the data scored zero, and a dash
                    would make "we never looked" indistinguishable from "we looked and found
                    nothing". */}
                {row.observed.state === 'unevaluated' && (
                  <span className="lq-muted">{t('contracts.terms.unevaluated')}</span>
                )}
                {row.observed.state === 'absent' && (
                  <span className="lq-ct__absent">{t(row.observed.key)}</span>
                )}
                {row.observed.state === 'measured' && row.observed.text}
              </td>
              <td className="lq-nowrap">
                {row.holds === null ? (
                  <span className="lq-muted">{t('contracts.terms.unevaluated')}</span>
                ) : (
                  <span
                    className={row.holds ? 'lq-ct__holds' : 'lq-ct__breaks'}
                    aria-label={t(
                      row.holds ? 'contracts.terms.held' : 'contracts.terms.broke',
                    )}
                  >
                    {row.holds ? '✓ ' : '✕ '}
                    {t(row.holds ? 'contracts.terms.held' : 'contracts.terms.broke')}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="lq-note">{t('contracts.terms.onlyCarried')}</p>
    </div>
  );
}

export function ContractDetail(): ReactElement {
  const { t } = useTranslation();
  const { contractId = '' } = useParams();
  const contract = useContract(contractId);
  const identity = useIdentity();
  const lifecycle = useContractLifecycle();
  const sign = useSignContract();
  const evaluate = useEvaluateContract();
  const [outcome, setOutcome] = useState<EvaluationOutcome | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const now = new Date();

  const crumb = (
    <div className="lq-cp__crumb">
      <Link to={SCREEN_PATHS.contracts}>{t('contracts.title')}</Link>
      {' / '}
      {contract.isSuccess ? contract.data.name : contractId}
    </div>
  );

  if (contract.isPending) {
    return (
      <div className="lq-ct lq-refscreen">
        {crumb}
        <SkeletonRows rows={4} columns={4} />
      </div>
    );
  }
  if (contract.isError) {
    return (
      <div className="lq-ct lq-refscreen">
        {crumb}
        <EmptyState
          title={t('contracts.notFound.title')}
          guidance={contract.error.message}
        />
      </div>
    );
  }

  const found = contract.data;
  const busy = lifecycle.isPending || sign.isPending || evaluate.isPending;
  const failure = lifecycle.error ?? sign.error ?? evaluate.error;

  const run = (action: ContractAction): void => {
    setNote(null);
    if (action === 'submit') {
      lifecycle.mutate(
        { id: found.id, verb: 'submit' },
        {
          onSuccess: () => {
            setNote('contracts.acted.submitted');
          },
        },
      );
      return;
    }
    if (action === 'activate') {
      lifecycle.mutate(
        { id: found.id, verb: 'activate' },
        {
          onSuccess: () => {
            setNote('contracts.acted.activated');
          },
        },
      );
      return;
    }
    if (action === 'retire') {
      lifecycle.mutate(
        { id: found.id, verb: 'retire', reason: t('contracts.reason.retire') },
        {
          onSuccess: () => {
            setNote('contracts.acted.retired');
          },
        },
      );
      return;
    }
    if (action === 'evaluate') {
      evaluate.mutate(found.id, {
        onSuccess: (result) => {
          setOutcome(result);
          setNote(result.held ? 'contracts.acted.held' : 'contracts.acted.breached');
        },
      });
      return;
    }
    const side = action === 'sign_producer' ? 'producer' : 'consumer';
    sign.mutate(
      { id: found.id, side, reason: t('contracts.reason.sign') },
      {
        onSuccess: () => {
          setNote(`contracts.acted.signed.${side}`);
        },
      },
    );
  };

  const gate = gateFor(found, (action) => can(identity.data, NEEDS[action]));
  const outstanding = outstandingSides(found);

  return (
    <div className="lq-ct lq-refscreen">
      {crumb}

      <div className="lq-cp__detailHead">
        <div className="lq-cp__detailTitle">
          <h1 className="lq-cp__h1">{found.name}</h1>
          <div className="lq-cp__detailDesc">
            {t('contracts.subline', {
              producer: found.producer_subject,
              consumer: found.consumer_subject,
              severity: t(`contracts.severity.${found.breach_severity}`).toLowerCase(),
            })}
          </div>
        </div>
        <span className={`lq-tag lq-tag--ct-${toneOf(found.state)} lq-ct__stateChip`}>
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(stateLabelKey(found.state))}
        </span>
      </div>

      {/* The state's meaning, tinted to match its chip. On the page because it is the sentence a
          reader needs before they wonder why there is no verdict below. */}
      <div className={`lq-band lq-band--ct-${toneOf(found.state)} lq-ct__hint`}>
        {t(stateHintKey(found.state))}
      </div>

      <div className="lq-ct__actions">
        {gate.offered.map((each) => (
          <button
            key={each.action}
            type="button"
            className={each.primary ? 'lq-ct__act lq-ct__act--primary' : 'lq-ct__act'}
            disabled={busy}
            onClick={() => {
              run(each.action);
            }}
          >
            {t(`contracts.action.${each.action}`)}
          </button>
        ))}
        {gate.withheld.map((action) => (
          /* Dashed with a hollow dot, deliberately unlike the solid-bordered chip beside it. This
             action is possible right now and belongs to somebody else; that one is not possible at
             all. Two different sentences, and a reader has to be able to tell without hovering. */
          <span
            key={action}
            className="lq-ct__rolegated"
            title={t(`contracts.withheld.${action}`)}
          >
            <span className="lq-ct__restrictedDot" aria-hidden="true" />
            {t(`contracts.action.${action}`)}
            {t('contracts.withheld.suffix')}
          </span>
        ))}
        {blockedFor(found).map((key) => (
          /* Named with its reason rather than absent: a missing Evaluate on a retired contract reads
             as an oversight, where the sentence is the platform explaining itself. */
          <span key={key} className="lq-ct__blocked" title={t(`${key}.why`)}>
            {t(key)}
          </span>
        ))}
      </div>

      {failure !== null && (
        <Alert tone="error" title={t('contracts.action.failed')}>
          {failure.message}
        </Alert>
      )}
      {note !== null && (
        <div className="lq-flash" role="status">
          <span className="lq-flash__body">{t(note)}</span>
        </div>
      )}

      <div className="lq-sect">
        <h2 className="lq-sect__title">{t('contracts.terms.section')}</h2>
      </div>
      <Terms contract={found} evaluation={outcome} />

      {/* Side by side, because they are the two halves of one question: what was promised by whom,
          and whether it is holding. Stacked, a reader scrolls past the signatures to reach the
          verdict and loses which parties it is a verdict about. */}
      <div className="lq-ct__pair">
        <section className="lq-ct__panel">
          <h2 className="lq-ct__panelTitle">{t('contracts.signoff.section')}</h2>
          <ul className="lq-ct__signoffs">
            {signoffsOf(found).map((signoff) => (
              <li key={signoff.side} className="lq-ct__signoff">
                <span className="lq-ct__signoffSide">
                  {t(`contracts.side.${signoff.side}`)}
                </span>
                <span className="lq-ct__signoffWho">
                  <span
                    className={signoff.signedBy === null ? 'lq-ct__unsigned' : undefined}
                  >
                    {signoff.signedBy ?? t('contracts.signoff.notSigned')}
                  </span>
                  <span className="lq-ct__signoffParty lq-mono">{signoff.subject}</span>
                </span>
                <span className="lq-ct__signoffWhen">
                  {signoff.signedAt ?? (
                    <span className="lq-ct__unsigned">
                      {t('contracts.signoff.outstanding')}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <p className="lq-note">
            {outstanding.length === 0
              ? t('contracts.signoff.bothSigned')
              : t('contracts.signoff.waiting', {
                  sides: outstanding
                    .map((side) => t(`contracts.side.${side}`).toLowerCase())
                    .join(' & '),
                })}
          </p>
        </section>

        <section className="lq-ct__panel">
          <h2 className="lq-ct__panelTitle">{t('contracts.evaluation.section')}</h2>
          {outcome === null ? (
            /* Deliberately not "held". Nothing stored is readable — only a run made here — so this
               says no evaluation has been seen rather than that the terms are fine. */
            <>
              <div className="lq-cp__noneTitle">{t('contracts.evaluation.none')}</div>
              <div className="lq-cp__noneBody">
                {t(
                  found.state === 'active'
                    ? 'contracts.evaluation.noneActive'
                    : 'contracts.evaluation.noneInactive',
                  { state: t(stateLabelKey(found.state)).toLowerCase() },
                )}
              </div>
            </>
          ) : (
            <>
              <div className={outcome.held ? 'lq-ct__verdictOk' : 'lq-ct__verdictBad'}>
                {t(
                  outcome.held
                    ? 'contracts.evaluation.held'
                    : 'contracts.evaluation.broke',
                  { count: outcome.breaches.length },
                )}
              </div>
              {outcome.breaches.map((breach) => (
                <div key={breach.term} className="lq-ct__breach">
                  <span className="lq-mono lq-ct__breachCause">{breach.cause}</span>
                  <div className="lq-cell__secondary">{breach.summary}</div>
                </div>
              ))}
              {/* The deduplication sentence, which is the one an operator misreads without: `false`
                  beside breaches means an incident is already open, not that nothing was raised. */}
              <p className="lq-note">
                {t(
                  outcome.held
                    ? 'contracts.evaluation.noIncident'
                    : outcome.incident_opened
                      ? 'contracts.evaluation.opened'
                      : 'contracts.evaluation.deduplicated',
                )}
              </p>
            </>
          )}
        </section>
      </div>

      <ContractHistory contractId={found.id} now={now} />
    </div>
  );
}
